// LocalStorage-backed hooks for Bookmarks & Progress
import { useState, useEffect, useCallback } from 'react';

const BM_KEY = 'gsk_bookmarks_v1';
const PG_KEY = 'gsk_progress_v1';
const ST_KEY = 'gsk_streak_v1';

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function useBookmarks() {
  const [items, setItems] = useState(() => readJSON(BM_KEY, []));

  useEffect(() => {
    localStorage.setItem(BM_KEY, JSON.stringify(items));
  }, [items]);

  const isBookmarked = useCallback((id) => items.some((b) => b.id === id), [items]);

  const toggle = useCallback((item) => {
    setItems((prev) => {
      const exists = prev.find((b) => b.id === item.id);
      if (exists) return prev.filter((b) => b.id !== item.id);
      return [{ ...item, addedAt: Date.now() }, ...prev];
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((b) => b.id !== id));
  }, []);

  return { bookmarks: items, isBookmarked, toggle, remove };
}

export function useProgress() {
  const [progress, setProgress] = useState(() => readJSON(PG_KEY, {}));

  useEffect(() => {
    localStorage.setItem(PG_KEY, JSON.stringify(progress));
  }, [progress]);

  const markRead = useCallback((id) => {
    setProgress((prev) => ({ ...prev, [id]: { read: true, at: Date.now() } }));
  }, []);

  const unmark = useCallback((id) => {
    setProgress((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const isRead = useCallback((id) => Boolean(progress[id]?.read), [progress]);

  const readCount = Object.keys(progress).length;

  return { progress, isRead, markRead, unmark, readCount };
}

export function useStreak() {
  const [state, setState] = useState(() => readJSON(ST_KEY, { streak: 0, lastDate: null }));

  const touch = useCallback(() => {
    setState((prev) => {
      const today = new Date().toDateString();
      if (prev.lastDate === today) return prev;
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      const newStreak = prev.lastDate === yesterday ? (prev.streak || 0) + 1 : 1;
      const next = { streak: newStreak, lastDate: today };
      localStorage.setItem(ST_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => { touch(); }, [touch]);

  return { streak: state.streak || 0, lastDate: state.lastDate };
}
